import React from 'react';
import { Typography, RegularButton } from 'fronton-react';
import { cx } from 'linaria';
import { CategorySuggestion } from 'types/suggestions';
import { capitalize } from 'utils/capitalize';
import { useTranslation } from 'react-i18next';
import { patchUrlByLocale } from 'utils';
import { useSuggestionsButtonAction } from '../../hooks/use-suggestions-button-action';
import {
  suggestion,
  suggestionButton,
  suggestionText,
  suggestionCaption,
  suggestionContentWrap,
  suggestionTextContentWrap,
} from './suggestion-list-item-styles';
import { SuggestionListItemNewImage } from './suggestion-list-item-new-image';
import { ArrowUpRightIcon } from '@fronton/icons-react';
import { SuggestionListItemOption } from './suggestion-list-item-option';

const DATA_QA_CATEGORY_SUGGESTION_BUTTON = 'category-suggestion-button';

interface SuggestionListItemCategoriesProps {
  categorySuggestion: CategorySuggestion;
  elementIndex: number;
  navigationIndex: number;
  isSrp: boolean;
  suggestionSelector: string;
  buttonAction: ReturnType<typeof useSuggestionsButtonAction>;
}

export const SuggestionListItemCategory: React.FC<SuggestionListItemCategoriesProps> = ({
  categorySuggestion,
  elementIndex,
  navigationIndex,
  isSrp,
  buttonAction,
  suggestionSelector,
}) => {
  const { t } = useTranslation();

  const { categoryUrl, categoryName, categoryLabel, categoryIcon } = categorySuggestion;

  const buttonParams = {
    ...buttonAction(
      elementIndex,
      categoryUrl,
      {
        block: t('header.searchbar.categories'),
        index: elementIndex,
        text: categoryName,
      },
      capitalize(categoryName),
      categoryUrl,
    ),
  };

  return (
    <SuggestionListItemOption elementIndex={elementIndex} navigationIndex={navigationIndex}>
      <RegularButton
        variant="pseudo"
        tabIndex={-1}
        className={cx(
          suggestionSelector,
          suggestion,
          elementIndex === navigationIndex && 'active',
          isSrp && suggestionButton,
        )}
        key={categoryName + categoryLabel}
        data-qa={DATA_QA_CATEGORY_SUGGESTION_BUTTON}
        {...buttonParams}
        href={patchUrlByLocale({ url: categoryUrl })}
      >
        <div className={suggestionContentWrap}>
          <SuggestionListItemNewImage
            src={categoryIcon ?? null}
            alt={capitalize(categoryName)}
            fallback={<ArrowUpRightIcon type="outline" size="l" color="text-minor" />}
          />
          <div className={suggestionTextContentWrap}>
            <Typography variant="m" size="body_short" className={suggestionText} color="text-primary">
              {capitalize(categoryName)}
            </Typography>
            <Typography variant="caption" color="text-secondary" className={suggestionCaption}>
              {capitalize(categoryLabel)}
            </Typography>
          </div>
        </div>
      </RegularButton>
    </SuggestionListItemOption>
  );
};
